import type { IngestRequest, RequestPriority, RequestType } from '@feedback/shared';
import type { ApiClient } from './api/client';
import type { MetadataStore } from './metadata/collector';
import { attachmentKind } from './attachments/kind';
import { uploadFile } from './attachments/upload';
import { captureFile } from './attachments/capture-upload';
import type { AttachmentRef } from './attachments/upload';

/** The subset of `ApiClient` a submit needs — narrowed so tests can pass a minimal fake. */
type SubmitApi = Pick<ApiClient, 'createUpload' | 'putBlob' | 'submitRequest'>;

// Deliberately simple — matches the shared `ingestRequestSchema`'s `reporterEmail: z.string()
// .email().optional()` closely enough to keep an obviously-invalid value (e.g. a display name
// or a typo missing the "@") out of the payload. Doesn't need to be RFC-5322-exact: an email
// that slips past this but still fails the backend's real check would fail submitRequest()
// like any other bad payload — the point here is only to avoid the *permanent*, always-repeats
// failure of shipping a value that's obviously not an email on every retry.
const EMAIL_LIKE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export interface SubmitForm {
  type: RequestType;
  priority: RequestPriority;
  title: string;
  description: string;
}

/** One marker on the capture, already normalized to 0..1 by `CaptureStage`. */
export interface SubmitPin {
  x: number;
  y: number;
  text: string;
}

export interface SubmitFeedbackOptions {
  api: SubmitApi;
  metadata: MetadataStore;
  form: SubmitForm;
  /** Selected files, screenshots tagged via `attachments/kind` — see `Modal.tsx`. */
  attachments: File[];
  /**
   * The capture that led the modal, with its markers (I19222).
   *
   * Separate from `attachments` all the way down rather than merged in early,
   * because the pins have to travel WITH the one attachment they belong to and
   * a `File` has nowhere to carry them. It is uploaded first, so a report whose
   * screenshot is the whole point does not lose it to a later attachment
   * failing.
   */
  capture?: { blob: Blob; pins: SubmitPin[] } | null;
  /**
   * Resolves the capture's upload, normally one `CaptureUploads.start` began
   * when the capture landed (I24294, ADR 0045), so Send does not wait on
   * megabytes it could have sent while the reporter typed. Absent, the capture
   * is uploaded here, at Send, as it always was.
   */
  uploadCapture?: (blob: Blob) => Promise<AttachmentRef>;
}

export interface SubmitFeedbackResult {
  reference: string;
  id: string;
}

/**
 * Runs the full submit: uploads every pending attachment (sequentially, so a failure partway
 * through never fires `submitRequest` — no partial submit reaches the backend), then POSTs the
 * assembled `IngestRequest` and returns the backend's `{ reference, id }`. The leading capture
 * is normally already uploaded by then (`uploadCapture`, ADR 0045), so it costs Send nothing.
 *
 * Reporter identity (`reporterName`/`reporterEmail`/`reporterUserId`) is derived from whatever
 * the host app passed to `identify()` (surfaced as `metadata.collect().user`) — fields the host
 * never called `identify()` with are simply omitted, matching `ingestRequestSchema`'s optional
 * reporter fields.
 *
 * Never swallows errors itself — an upload failure or a `submitRequest` rejection (typed
 * `AuthError`/`RateLimitError`/`ApiError` from `./api/types`, or a plain network error) rejects
 * this promise for the caller (`Modal`'s submit handler) to turn into the error/retry UI state.
 */
export async function submitFeedback({
  api,
  metadata,
  form,
  attachments,
  capture,
  uploadCapture,
}: SubmitFeedbackOptions): Promise<SubmitFeedbackResult> {
  const refs: AttachmentRef[] = [];

  // FIRST, before the picked files. The uploads are sequential and a failure
  // part way through aborts the whole submit, so whatever goes first is the
  // thing most likely to survive a bad connection — and on a report filed
  // through the widget the screenshot is usually the report.
  if (capture) {
    const ref = uploadCapture
      ? await uploadCapture(capture.blob)
      : await uploadFile(api, captureFile(capture.blob), 'SCREENSHOT');
    // Markers ride on the attachment they were placed on rather than beside
    // the request, so nothing downstream has to guess which image they mean
    // (ADR 0030). Empty notes are kept: a numbered marker with nothing typed
    // against it still says "look here".
    refs.push(capture.pins.length > 0 ? { ...ref, pins: capture.pins } : ref);
  }

  for (const file of attachments) {
    // Sequential (not Promise.all): keeps upload order deterministic and avoids firing every
    // remaining upload's network request once one has already failed.
    // Sequential on purpose: this is a retry loop, so each attempt has to
    // know the previous one failed before it starts.
    refs.push(await uploadFile(api, file, attachmentKind(file)));
  }

  const collected = metadata.collect();
  const user = collected.user as
    | { userId?: unknown; name?: unknown; email?: unknown }
    | undefined;

  const payload: IngestRequest = {
    type: form.type,
    priority: form.priority,
    title: form.title,
    description: form.description,
    metadata: collected as unknown as Record<string, unknown>,
    attachments: refs,
    ...(typeof user?.name === 'string' && user.name ? { reporterName: user.name } : {}),
    // `reporterEmail` is validated with `.email()` by the shared schema — an invalid value
    // here would make the backend reject the whole request with a 400 on every retry (the
    // rest of the payload is otherwise fine). Since the field is optional, silently omitting
    // an unparseable email is strictly better than a permanent submit failure.
    ...(typeof user?.email === 'string' && EMAIL_LIKE.test(user.email) ? { reporterEmail: user.email } : {}),
    ...(typeof user?.userId === 'string' && user.userId ? { reporterUserId: user.userId } : {}),
  };

  return api.submitRequest(payload);
}
